import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  LocationOn,
  CalendarToday,
  AttachMoney,
  People,
  Assignment,
  Security,
  Schedule,
  TrendingUp,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../services/config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const mapProject = (p: any) => {
      const loc = p?.location;
      const location = typeof loc === 'string'
        ? loc
        : loc?.address
          ? [loc.address.street, loc.address.city, loc.address.state].filter(Boolean).join(', ')
          : '';
      const mgr = p?.manager;
      let firstName = 'M';
      let lastName = '';
      let email = '';
      let avatar = null;
      if (typeof mgr === 'string') {
        firstName = 'Manager';
      } else if (mgr?.firstName || mgr?.lastName) {
        firstName = mgr.firstName || 'M';
        lastName = mgr.lastName || '';
        email = mgr.email || '';
        avatar = mgr.avatar || null;
      } else if (mgr?.name) {
        const parts = String(mgr.name).split(' ');
        firstName = parts[0] || 'M';
        lastName = parts.slice(1).join(' ') || '';
        email = mgr.email || '';
        avatar = mgr.avatar || null;
      }
      const budgetVal = typeof p?.budget === 'number' ? p.budget : (p?.budget?.total ?? p?.budget ?? 0);
      const spentVal = typeof p?.actualCost === 'number' ? p.actualCost : (p?.budget?.spent ?? 0);
      return {
        _id: p?._id || p?.id || id,
        name: p?.name || p?.title || 'Untitled Project',
        description: p?.description || '',
        status: p?.status || 'planning',
        startDate: p?.startDate || p?.timeline?.startDate || new Date().toISOString(),
        endDate: p?.endDate || p?.timeline?.endDate || new Date().toISOString(),
        location,
        manager: { firstName, lastName, email, avatar },
        budget: Number(budgetVal) || 0,
        spent: Number(spentVal) || 0,
        progress: typeof p?.progress === 'number' ? p.progress : 0,
        team: Array.isArray(p?.team) ? p.team : [],
        recentActivities: Array.isArray(p?.recentActivities) ? p.recentActivities : [],
        milestones: Array.isArray(p?.milestones) ? p.milestones : [],
        documents: Array.isArray(p?.documents) ? p.documents : [],
      };
    };

    const fetchProject = async () => {
      if (!id) return;
      // Validate Mongo ObjectId to avoid 400 CastError responses
      if (!/^[a-fA-F0-9]{24}$/.test(String(id))) {
        setError('Invalid project ID');
        setProject(null);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/projects/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Failed to fetch project');
        const payload = await res.json();
        const p = payload?.data || payload?.project || payload;
        setProject(mapProject(p));
      } catch (err: any) {
        console.error('Error loading project', err);
        setError(err?.message || 'Failed to load project');
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'planning':
        return 'warning';
      case 'completed':
        return 'info';
      case 'on-hold':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'milestone':
        return <CheckCircle color="success" />;
      case 'safety':
        return <Security color="warning" />;
      case 'report':
        return <Assignment color="info" />;
      default:
        return <TrendingUp />;
    }
  };

  if (loading || !project) {
    return (
      <Box sx={{ p: 3 }}>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Typography>Loading project details...</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/projects')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {project.name}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/projects/${id}/edit`)}
        >
          Edit Project
        </Button>
      </Box>

      {/* Project Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 2 }}>
              <Typography variant="h6" gutterBottom>
                Project Overview
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {project.description}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  icon={<LocationOn />}
                  label={project.location}
                  variant="outlined"
                />
                <Chip
                  icon={<CalendarToday />}
                  label={`${project.startDate} - ${project.endDate}`}
                  variant="outlined"
                />
                <Chip
                  label={project.status.toUpperCase()}
                  color={getStatusColor(project.status)}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Progress: {project.progress}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={project.progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                  src={project.manager.avatar}
                >
                  {project.manager.firstName[0]}{project.manager.lastName[0]}
                </Avatar>
                <Typography variant="h6">
                  {project.manager.firstName} {project.manager.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Project Manager
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.manager.email}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Budget:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  ${project.budget.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Spent:</Typography>
                <Typography variant="body2" color="warning.main">
                  ${project.spent.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Remaining:</Typography>
                <Typography variant="body2" color="success.main">
                  ${(project.budget - project.spent).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Team" />
            <Tab label="Milestones" />
            <Tab label="Recent Activity" />
            <Tab label="Documents" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Project Team
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {project.team.map((member: any, index: number) => (
              <Box key={index}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}>
                      {member.name.split(' ').map((n: string) => n[0]).join('')}
                    </Avatar>
                    <Typography variant="subtitle1">{member.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.role}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Project Milestones
          </Typography>
          <List>
            {project.milestones.map((milestone: any, index: number) => (
              <ListItem key={index}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: milestone.completed ? 'success.main' : 'grey.300' }}>
                    {milestone.completed ? <CheckCircle /> : <Schedule />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={milestone.name}
                  secondary={`Target Date: ${milestone.date}`}
                />
                <Chip
                  label={milestone.completed ? 'Completed' : 'Pending'}
                  color={milestone.completed ? 'success' : 'default'}
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <List>
            {project.recentActivities.map((activity: any) => (
              <ListItem key={activity.id}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={activity.description}
                  secondary={`${activity.user} • ${activity.date}`}
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Project Documents
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {project.documents.map((doc: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>
                      <Chip label={doc.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{doc.size}</TableCell>
                    <TableCell>{doc.date}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined">
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ProjectDetail;